import { MouseEvent, useState } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import MoreVert from '@mui/icons-material/MoreVert';

export const SHOW_DISABLED_OPTION = "Show option but don't allow selection";
export const HIDE_DISABLED_OPTION = 'Hide option and disable';
export const SAVE_BEFORE_DELETE =
  'Save the draft to delete this disabled option';
export const DISABLE_BEFORE_DELETE = 'To delete this option, first disable it';
export const LAST_OPTION = 'A field needs at least one option';

interface OptionActionsMenuProps {
  /** The option's name, for the button's label. */
  name: string;
  disabled: boolean;
  /** Google already has the option disabled, so it can be deleted. */
  savedDisabled: boolean;
  /** The field's only option can't be deleted. */
  isLastOption: boolean;
  /** Disables the option; true keeps it visible (not selectable) when the label is applied. */
  onDisable: (showInApply: boolean) => void;
  onEnable: () => void;
  onDelete: () => void;
}

/** The ⋮ menu of a published option: disable it (shown or hidden), enable it, or delete it. */
export default function OptionActionsMenu({
  name,
  disabled,
  savedDisabled,
  isLastOption,
  onDisable,
  onEnable,
  onDelete,
}: OptionActionsMenuProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const close = () => setAnchorEl(null);
  const run = (action: () => void) => () => {
    close();
    action();
  };

  // Google only deletes a published option it already has disabled.
  const deleteBlockedReason = isLastOption
    ? LAST_OPTION
    : !disabled
    ? DISABLE_BEFORE_DELETE
    : !savedDisabled
    ? SAVE_BEFORE_DELETE
    : undefined;

  const deleteItem = (
    <MenuItem disabled={!!deleteBlockedReason} onClick={run(onDelete)}>
      Delete
    </MenuItem>
  );

  return (
    <>
      <IconButton
        size="small"
        aria-label={`Actions for option ${name}`}
        onClick={(event: MouseEvent<HTMLElement>) =>
          setAnchorEl(event.currentTarget)
        }
      >
        <MoreVert />
      </IconButton>
      <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={close}>
        {disabled ? (
          <MenuItem onClick={run(onEnable)}>Enable</MenuItem>
        ) : (
          [
            <MenuItem key="show" onClick={run(() => onDisable(true))}>
              {SHOW_DISABLED_OPTION}
            </MenuItem>,
            <MenuItem key="hide" onClick={run(() => onDisable(false))}>
              {HIDE_DISABLED_OPTION}
            </MenuItem>,
          ]
        )}
        {deleteBlockedReason ? (
          // A greyed-out item ignores the pointer, so the reason sits on a wrapper.
          <Tooltip title={deleteBlockedReason} placement="left">
            <Box component="span">{deleteItem}</Box>
          </Tooltip>
        ) : (
          deleteItem
        )}
      </Menu>
    </>
  );
}
